import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDataContext } from "../contexts/AuthContext";
import { fetchAccountsExport } from "../services/api.js";
import logoPng from "../images/Frame.png";

const money = (value) =>
  `$${Number(value || 0).toLocaleString("es-AR", { maximumFractionDigits: 0 })}`;

const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt)) return "—";
  return dt.toLocaleDateString("es-AR", { timeZone: "UTC" });
};

// Mismo set de estados que el backoffice (OrderAccountService).
const FILTER_OPTIONS = [
  { value: "all", label: "Todos" },
  { value: "demorado", label: "Demorado" },
  { value: "al_dia", label: "Al día" },
  { value: "saldado", label: "Saldado" },
  { value: "sin_prenda", label: "Sin entrar" },
];

const estadoStyle = (e) => {
  if (e === "demorado") return { bg: "#fee2e2", fg: "#b91c1c", label: "Demorado" };
  if (e === "saldado") return { bg: "#dcfce7", fg: "#15803d", label: "Saldado" };
  if (e === "sin_prenda") return { bg: "#f1f5f9", fg: "#64748b", label: "Sin entrar" };
  return { bg: "#dbeafe", fg: "#1d4ed8", label: "Al día" };
};

const C = {
  page: { padding: "1.1em 1em 2em", maxWidth: 960, margin: "0 auto" },
  h1: { margin: "0 0 .15em", fontFamily: '"Vinila Extended Bold", sans-serif', fontSize: "1.5em", color: "#0f172a" },
  sub: { margin: "0 0 1em", color: "#64748b", fontSize: ".95em" },
  toolbar: { display: "flex", gap: ".6em", flexWrap: "wrap", marginBottom: "1em" },
  input: { flex: "1 1 12em", minWidth: 0, borderRadius: ".7em", border: "1px solid #cbd5e1", background: "#fff", padding: ".55em .8em", fontSize: ".9em", color: "#0f172a" },
  select: { borderRadius: ".7em", border: "1px solid #cbd5e1", background: "#fff", padding: ".55em .8em", fontSize: ".9em", color: "#0f172a" },
  totalsBar: { display: "flex", gap: ".5em", flexWrap: "wrap", marginBottom: ".9em" },
  totalPill: { background: "#f8fafc", border: "1px solid #e9eef5", borderRadius: ".7em", padding: ".5em .8em", fontSize: ".8em", color: "#334155" },
  totalNum: { fontWeight: 800, color: "#0f172a" },
  tableWrap: { overflowX: "auto", border: "1px solid #e9eef5", borderRadius: "1em", background: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,.06)" },
  table: { borderCollapse: "collapse", width: "100%", fontSize: ".82em", minWidth: 720 },
  th: { textAlign: "left", padding: ".6em .7em", background: "#f8fafc", color: "#64748b", fontWeight: 800, fontSize: ".82em", textTransform: "uppercase", letterSpacing: ".03em", whiteSpace: "nowrap", borderBottom: "1px solid #e9eef5" },
  thR: { textAlign: "right" },
  td: { padding: ".55em .7em", borderBottom: "1px solid #f1f5f9", color: "#334155", verticalAlign: "top" },
  tdR: { textAlign: "right", whiteSpace: "nowrap", fontFamily: "monospace" },
  badge: (bg, fg) => ({ display: "inline-block", background: bg, color: fg, borderRadius: 999, padding: ".1em .55em", fontSize: ".82em", fontWeight: 800, whiteSpace: "nowrap" }),
  empty: { textAlign: "center", color: "#94a3b8", padding: "2em 0", fontSize: ".95em" },
  buyBtn: { width: "100%", marginTop: "1.1em", border: "none", borderRadius: ".8em", background: "#d54ab7", color: "#fff", padding: ".8em", fontWeight: 800, fontSize: ".95em", cursor: "pointer" },
};

const selectionText = (sel) =>
  Array.isArray(sel) && sel.length
    ? sel.map((s) => `${s.quantity}× ${s.articulo}`).join("; ")
    : "—";

function AccountsSummary() {
  const navigate = useNavigate();
  const { contextData } = useDataContext();

  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchAccountsExport(contextData.customer.order_id);
      setRows(Array.isArray(data?.rows) ? data.rows : []);
    } catch (error) {
      console.error("Error fetching accounts export:", error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [contextData.customer.order_id]);

  useEffect(() => {
    if (contextData.customer && contextData.customer.order_id) fetchData();
  }, [contextData.customer, contextData.customer.order_id, fetchData]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter !== "all" && r.estado !== filter) return false;
      if (term && !String(r.nombre || "").toLowerCase().includes(term)) return false;
      return true;
    });
  }, [rows, filter, search]);

  const totals = useMemo(() => {
    return filteredRows.reduce(
      (acc, r) => {
        acc.vencidas += Number(r.vencidas || 0);
        acc.vencido += Number(r.vencido || 0);
        acc.total += Number(r.total || 0);
        acc.pagado += Number(r.pagado || 0);
        return acc;
      },
      { vencidas: 0, vencido: 0, total: 0, pagado: 0 }
    );
  }, [filteredRows]);

  // El tutor pasa a modo "comprar" y vuelve al inicio a elegir sus prendas.
  const comprarPrenda = () => {
    try {
      localStorage.setItem(`mcb_tutor_mode_${contextData.customer.id}`, "buy");
      window.dispatchEvent(new Event("mcb-tutor-mode"));
    } catch (e) { /* ignore */ }
    navigate("/mcb");
  };

  if (loading) {
    return (
      <div className="contcargando">
        <img src={logoPng} alt="littleBlueLogo" className="imgcargando" />
        <h2 className="text-muted">CARGANDO...</h2>
        <div className="loader"></div>
      </div>
    );
  }

  return (
    <div style={C.page}>
      <h1 style={C.h1}>Alumnos</h1>
      <p style={C.sub}>Estado de cuenta de cada alumno del curso.</p>

      <div style={C.toolbar}>
        <input
          type="text"
          placeholder="Buscar por nombre y apellido…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={C.input}
        />
        <select value={filter} onChange={(e) => setFilter(e.target.value)} style={C.select}>
          {FILTER_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      <div style={C.totalsBar}>
        <span style={C.totalPill}>Total a pagar: <span style={C.totalNum}>{money(totals.total)}</span></span>
        <span style={C.totalPill}>Pagado al día: <span style={C.totalNum}>{money(totals.pagado)}</span></span>
        <span style={C.totalPill}>Adeudado al día: <span style={C.totalNum}>{money(totals.vencido)}</span> ({totals.vencidas} cuota/s)</span>
      </div>

      {filteredRows.length === 0 ? (
        <div style={C.empty}>
          <i className="bi bi-inbox" style={{ fontSize: "1.6em", display: "block", marginBottom: ".3em" }} />
          No hay alumnos para mostrar con ese filtro.
        </div>
      ) : (
        <div style={C.tableWrap}>
          <table style={C.table}>
            <thead>
              <tr>
                <th style={C.th}>Nombre</th>
                <th style={C.th}>Selección</th>
                <th style={C.th}>Forma de pago</th>
                <th style={{ ...C.th, ...C.thR }}>Cuotas vencidas</th>
                <th style={{ ...C.th, ...C.thR }}>Adeudado al día</th>
                <th style={C.th}>Última cuota vencida</th>
                <th style={{ ...C.th, ...C.thR }}>Total a pagar</th>
                <th style={{ ...C.th, ...C.thR }}>Pagado al día</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((r, i) => {
                const st = estadoStyle(r.estado);
                return (
                  <tr key={r.customer_id ?? i}>
                    <td style={C.td}>
                      <div style={{ fontWeight: 700, color: "#0f172a" }}>{r.nombre}</div>
                      <span style={C.badge(st.bg, st.fg)}>{st.label}</span>
                    </td>
                    <td style={{ ...C.td, minWidth: 160 }}>{selectionText(r.seleccion)}</td>
                    <td style={C.td}>{r.pago || "—"}</td>
                    <td style={{ ...C.td, ...C.tdR }}>{r.vencidas || "—"}</td>
                    <td style={{ ...C.td, ...C.tdR }}>{r.vencido > 0 ? money(r.vencido) : "—"}</td>
                    <td style={C.td}>{fmtDate(r.ult_venc)}</td>
                    <td style={{ ...C.td, ...C.tdR }}>{money(r.total)}</td>
                    <td style={{ ...C.td, ...C.tdR }}>{money(r.pagado)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <button onClick={comprarPrenda} style={C.buyBtn}>
        <i className="bi bi-bag-plus" /> Comprar prendas
      </button>
    </div>
  );
}

export default AccountsSummary;
